// Export helpers for the print module: Excel, PDF, share, download.

import { paperSize } from './paper';

function xmlEsc(v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ---- Excel (SpreadsheetML 2003: opens in Excel / LibreOffice, no library) ----
// Uses the template's visible columns, order and labels; numbers stay numeric.
export function toExcel({ template, columns, rows, ctx }) {
  var t = template;
  var rtl = t.direction === 'auto' ? !!ctx.rtl : t.direction === 'rtl';
  var cols = t.columns
    .filter(function (c) { return c.visible; })
    .map(function (c) {
      var def = columns.find(function (d) { return d.key === c.key; });
      return def ? Object.assign({}, def, { label: c.label || def.label, width: c.width || 1 }) : null;
    })
    .filter(Boolean);
  if (t.showRowNumber) cols.unshift({ key: '__row', label: ctx.labels.row || '#', format: 'number', width: 0.45 });
  var numeric = function (c) { return c.format === 'money' || c.format === 'number'; };
  var sums = {};
  var cell = function (c, v) {
    if (numeric(c) && v !== '' && v != null && !isNaN(Number(v))) {
      return '<Cell ss:StyleID="' + (c.format === 'money' ? 'money' : 'num') + '"><Data ss:Type="Number">' + Number(v) + '</Data></Cell>';
    }
    return '<Cell><Data ss:Type="String">' + xmlEsc(v) + '</Data></Cell>';
  };
  var body = rows.map(function (r, i) {
    return '<Row>' + cols.map(function (c) {
      var v = c.key === '__row' ? i + 1 : c.value ? c.value(r) : r[c.key];
      if (c.sum) sums[c.key] = (sums[c.key] || 0) + (Number(v) || 0);
      return cell(c, v);
    }).join('') + '</Row>';
  }).join('');
  var hasSums = cols.some(function (c) { return c.sum; });
  var total = hasSums
    ? '<Row>' + cols.map(function (c, i) {
        if (c.sum) return '<Cell ss:StyleID="totalMoney"><Data ss:Type="Number">' + (sums[c.key] || 0) + '</Data></Cell>';
        return '<Cell ss:StyleID="total"><Data ss:Type="String">' + (i === 0 ? xmlEsc(ctx.labels.grandTotal) : '') + '</Data></Cell>';
      }).join('') + '</Row>'
    : '';
  var title = (t.header && t.header.title) || ctx.title || '';
  var xml =
    '<?xml version="1.0" encoding="UTF-8"?>' +
    '<?mso-application progid="Excel.Sheet"?>' +
    '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet" xmlns:x="urn:schemas-microsoft-com:office:excel">' +
    '<Styles>' +
    '<Style ss:ID="Default"><Font ss:FontName="Tahoma" ss:Size="10"/><Alignment ss:Vertical="Center"/></Style>' +
    '<Style ss:ID="title"><Font ss:FontName="Tahoma" ss:Size="13" ss:Bold="1"/></Style>' +
    '<Style ss:ID="sub"><Font ss:FontName="Tahoma" ss:Size="9" ss:Color="#64748B"/></Style>' +
    '<Style ss:ID="head"><Font ss:FontName="Tahoma" ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="' + t.theme.accent + '" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>' +
    '<Style ss:ID="num"><NumberFormat ss:Format="0"/></Style>' +
    '<Style ss:ID="money"><NumberFormat ss:Format="#,##0"/></Style>' +
    '<Style ss:ID="total"><Font ss:FontName="Tahoma" ss:Bold="1"/><Interior ss:Color="#EEF2F7" ss:Pattern="Solid"/></Style>' +
    '<Style ss:ID="totalMoney"><Font ss:FontName="Tahoma" ss:Bold="1"/><Interior ss:Color="#EEF2F7" ss:Pattern="Solid"/><NumberFormat ss:Format="#,##0"/></Style>' +
    '</Styles>' +
    '<Worksheet ss:Name="' + xmlEsc(String(title).slice(0, 30) || 'Sheet1') + '">' +
    '<Table>' +
    cols.map(function (c) { return '<Column ss:Width="' + Math.round(40 + c.width * 60) + '"/>'; }).join('') +
    '<Row ss:Height="22"><Cell ss:StyleID="title"><Data ss:Type="String">' + xmlEsc(title) + '</Data></Cell></Row>' +
    '<Row><Cell ss:StyleID="sub"><Data ss:Type="String">' + xmlEsc([ctx.company, ctx.printedAt, ctx.subtitle].filter(Boolean).join(' · ')) + '</Data></Cell></Row>' +
    '<Row/>' +
    '<Row ss:Height="20">' + cols.map(function (c) { return '<Cell ss:StyleID="head"><Data ss:Type="String">' + xmlEsc(c.label) + '</Data></Cell>'; }).join('') + '</Row>' +
    body + total +
    '</Table>' +
    '<WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">' + (rtl ? '<DisplayRightToLeft/>' : '') +
    '<FreezePanes/><FrozenNoSplit/><SplitHorizontal>4</SplitHorizontal><TopRowBottomPane>4</TopRowBottomPane></WorksheetOptions>' +
    '</Worksheet></Workbook>';
  return new Blob(['﻿' + xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
}

// ---- PDF: rasterise each rendered page (keeps Persian shaping and fonts exact) ----
export async function toPdf({ html, page, onProgress, isCancelled }) {
  var mods = await Promise.all([import('jspdf'), import('html2canvas')]);
  var JsPDF = mods[0].jsPDF || mods[0].default;
  var html2canvas = mods[1].default || mods[1];
  var size = paperSize(page);

  var frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;left:-10000px;top:0;border:0;width:' + (size.w + 20) + 'mm;height:' + (size.h + 20) + 'mm';
  document.body.appendChild(frame);
  try {
    var doc = frame.contentDocument;
    doc.open();
    doc.write(html.replace('</style>', 'body{background:#fff}.pm-page{margin:0}</style>'));
    doc.close();
    await new Promise(function (r) { setTimeout(r, 50); });
    if (doc.fonts && doc.fonts.ready) await doc.fonts.ready;
    await Promise.all(Array.prototype.map.call(doc.images, function (img) {
      return img.complete ? null : new Promise(function (r) { img.onload = img.onerror = r; });
    }));

    var pages = doc.querySelectorAll('.pm-page');
    var pdf = new JsPDF({
      orientation: size.w > size.h ? 'landscape' : 'portrait',
      unit: 'mm',
      format: [size.w, size.h],
      compress: true,
    });
    for (var i = 0; i < pages.length; i++) {
      if (isCancelled && isCancelled()) throw new Error('cancelled');
      if (onProgress) onProgress(i + 1, pages.length);
      var canvas = null;
      try {
        canvas = await nativeRaster(doc, pages[i], 2);
      } catch (e) {
        canvas = null;
      }
      // Fallback only: html2canvas breaks Persian letter joining and spacing.
      if (!canvas) canvas = await html2canvas(pages[i], { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false });
      var h = size.h;
      if (size.roll) {
        // Receipt paper: one sheet as tall as the content.
        h = (canvas.height / canvas.width) * size.w;
        pdf = new JsPDF({ orientation: h > size.w ? 'portrait' : 'landscape', unit: 'mm', format: [size.w, h], compress: true });
      } else if (i > 0) pdf.addPage([size.w, size.h], size.w > size.h ? 'landscape' : 'portrait');
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, size.w, h, undefined, 'FAST');
      canvas.width = canvas.height = 0; // free memory early
    }
    return pdf.output('blob');
  } finally {
    frame.remove();
  }
}

// ---- native rasterising: the browser draws the page itself (SVG foreignObject),
// so Persian shaping, spacing and fonts match the preview exactly. Fonts and
// images are embedded as data URLs (an SVG image can't load external files).
var dataUrlCache = {};
function toDataUrl(url) {
  if (!url || /^data:/i.test(url)) return Promise.resolve(url);
  if (!dataUrlCache[url]) {
    dataUrlCache[url] = fetch(url, { mode: 'cors', credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('fetch ' + r.status);
        return r.blob();
      })
      .then(function (b) {
        return new Promise(function (res, rej) {
          var fr = new FileReader();
          fr.onload = function () { res(fr.result); };
          fr.onerror = rej;
          fr.readAsDataURL(b);
        });
      })
      .catch(function () { delete dataUrlCache[url]; return null; });
  }
  return dataUrlCache[url];
}

async function inlineCssUrls(css) {
  var re = /url\((['"]?)([^'")]+)\1\)/g;
  var urls = [];
  var m;
  while ((m = re.exec(css))) if (!/^data:/i.test(m[2])) urls.push(m[2]);
  var map = {};
  await Promise.all(urls.map(function (u) { return toDataUrl(u).then(function (d) { map[u] = d; }); }));
  return css.replace(re, function (all, q, u) { return map[u] ? "url('" + map[u] + "')" : all; });
}

async function nativeRaster(doc, pageEl, scale) {
  var css = Array.prototype.map.call(doc.querySelectorAll('style'), function (s) { return s.textContent; }).join('\n');
  css = await inlineCssUrls(css);
  var clone = pageEl.cloneNode(true);
  var imgs = clone.querySelectorAll('img');
  await Promise.all(Array.prototype.map.call(imgs, function (img) {
    return toDataUrl(img.getAttribute('src')).then(function (d) {
      if (d) img.setAttribute('src', d);
      else img.remove();
    });
  }));
  var w = Math.ceil(pageEl.offsetWidth);
  var h = Math.ceil(pageEl.offsetHeight);
  var bs = doc.defaultView.getComputedStyle(doc.body);
  var root = doc.documentElement;
  var wrapStyle = 'margin:0;background:#fff;font-family:' + bs.fontFamily + ';color:' + bs.color +
    ';font-size:' + bs.fontSize + ';line-height:' + bs.lineHeight + ';direction:' + bs.direction + ';width:' + w + 'px;height:' + h + 'px';
  var xhtml = new XMLSerializer().serializeToString(clone);
  var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + w + '" height="' + h + '">' +
    '<foreignObject x="0" y="0" width="100%" height="100%">' +
    '<div xmlns="http://www.w3.org/1999/xhtml" dir="' + (root.getAttribute('dir') || 'rtl') + '" lang="' +
    (root.getAttribute('lang') || 'fa') + '" style="' + wrapStyle.replace(/"/g, "'") + '">' +
    '<style>' + css.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</style>' +
    xhtml + '</div></foreignObject></svg>';
  // data: URL, not blob: — Chrome taints canvases drawn from blob SVGs.
  var url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  {
    var img = new Image();
    await new Promise(function (res, rej) { img.onload = res; img.onerror = rej; img.src = url; });
    if (img.decode) await img.decode().catch(function () {});
    // Fonts inside an SVG image load async in some browsers; give them a tick.
    await new Promise(function (r) { setTimeout(r, 60); });
    var canvas = document.createElement('canvas');
    canvas.width = w * scale;
    canvas.height = h * scale;
    var ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0, w, h);
    canvas.toDataURL('image/jpeg', 0.1); // throws if tainted → caller falls back
    return canvas;
  }
}

// ---- download / share ----
export function download(blob, filename) {
  var url = URL.createObjectURL(blob);
  var a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(function () { URL.revokeObjectURL(url); }, 2000);
}

export function canShareFiles() {
  try {
    var f = new File(['x'], 'x.pdf', { type: 'application/pdf' });
    return !!(navigator.canShare && navigator.canShare({ files: [f] }));
  } catch (e) {
    return false;
  }
}

// Returns true when the system share sheet was used, false otherwise.
export async function shareFile(blob, filename, title) {
  var file = new File([blob], filename, { type: blob.type });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: title });
      return true;
    } catch (e) {
      if (e && e.name === 'AbortError') return true; // user closed the sheet
    }
  }
  return false;
}

export function safeFilename(name) {
  return String(name || 'report').replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '-').slice(0, 80);
}
